<template>
  <div v-if="dialogs.length">
    <template v-for="d in dialogs" :key="d.id">
      <v-dialog
        v-if="resolvePresentation(d) === 'lightbox'"
        :model-value="true"
        class="image-preview-overlay"
        content-class="image-preview-overlay-content"
        width="auto"
        max-width="calc(100vw - 32px)"
        scrim="black"
        :opacity="0.65"
        transition="fade-transition"
        data-test="central-image-preview-dialog"
        @update:model-value="val => handleClose(val, d.id)"
      >
        <component
          :is="d.component"
          v-bind="d.props"
          @close="() => handleCustomClose(d.id)"
        />
      </v-dialog>
      <!-- Standard Theme: Centered Modal -->
      <v-dialog
        v-if="resolvePresentation(d) === 'center'"
        :model-value="true"
        :transition="getConfig(d).transition"
        :content-class="getConfig(d).contentClass"
        :width="getConfig(d).width"
        :max-width="getConfig(d).maxWidth"
        :fullscreen="!!d.fullscreen"
        data-test="central-dialog"
        hide-header
        hide-footer
        centered
        no-close-on-backdrop
        :retain-focus="false"
        @update:model-value="val => handleClose(val, d.id)"
      >
        <component
          v-if="d.type === 'custom'"
          :is="d.component"
          v-bind="d.props"
          @close="() => handleCustomClose(d.id)"
          @action="payload => handleCustomAction(payload, d.id)"
        />
        <component
          v-else
          :is="resolveDialogComponent(d)"
          v-bind="d"
          @confirm="result => handleConfirm(result, d.id)"
          @cancel="() => handleCancel(d.id)"
        />
      </v-dialog>

      <!-- V2 Theme: Bottom Sheet Modal -->
      <v-dialog
        v-if="resolvePresentation(d) === 'sheet'"
        :model-value="true"
        :transition="getConfig(d).transition"
        :content-class="getConfig(d).contentClass"
        location="bottom"
        width="100%"
        data-test="central-dialog-v2"
        hide-header
        hide-footer
        class="dialog-bottom-sheet"
        :retain-focus="false"
        @update:model-value="val => handleClose(val, d.id)"
      >
        <component
          v-if="d.type === 'custom'"
          :is="d.component"
          v-bind="d.props"
          :is-bottom-sheet="true"
          @close="() => handleCustomClose(d.id)"
          @action="payload => handleCustomAction(payload, d.id)"
        />
        <component
          v-else
          :is="resolveDialogComponent(d)"
          v-bind="d"
          :is-bottom-sheet="true"
          @confirm="result => handleConfirm(result, d.id)"
          @cancel="() => handleCancel(d.id)"
        />
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import DialogAlert from "./DialogAlert.vue";
import DialogConfirm from "./DialogConfirm.vue";
import DialogError from "./DialogError.vue";

const componentMap = {
  alert: DialogAlert,
  confirm: DialogConfirm,
  error: DialogError
};

export default {
  name: "DialogContainer",
  components: {
    DialogAlert,
    DialogConfirm,
    DialogError
  },
  computed: {
    ...mapState("dialog", ["dialogs"]),
    isStandardV2Theme() {
      return this.$isStandardV2;
    },
    isStandard() {
      return !this.$isStandardV2;
    }
  },
  methods: {
    ...mapActions("dialog", ["closeDialog"]),
    resolveDialogComponent(d) {
      return componentMap[d?.type] || DialogAlert;
    },
    resolvePresentation(d) {
      if (d?.presentation === "lightbox") return "lightbox";
      if (d?.fullscreen) return "center";
      if (d?.presentation === "sheet" || d?.presentation === "center") {
        return d.presentation;
      }
      return this.isStandardV2Theme ? "sheet" : "center";
    },
    getConfig(d) {
      if (this.resolvePresentation(d) === "center") {
        return {
          transition: "slide-y-transition",
          contentClass: d?.contentClass || "dialog-v2-content",
          width: d?.width || "90%",
          maxWidth: d?.fullscreen ? undefined : d?.maxWidth || "500px"
        };
      }
      return {
        transition: "slide-y-reverse-transition",
        contentClass: d?.contentClass || "modal-qr-v2 modal-qr-v2--sheet",
        width: d?.width || "100%"
      };
    },
    closeById(id, result) {
      this.closeDialog({ __dialogId: id, result });
    },
    handleConfirm(result, id) {
      this.closeById(id, result !== false ? true : false);
    },
    handleCancel(id) {
      this.closeById(id, false);
    },
    handleClose(val, id) {
      if (!val) this.closeById(id, false);
    },
    handleCustomClose(id) {
      this.closeById(id, { action: "close" });
    },
    handleCustomAction(payload, id) {
      this.closeById(id, payload || { action: "confirm" });
    }
  }
};
</script>

<style scoped lang="scss">
.dialog-bottom-sheet {
  border-radius: 24px 24px 0 0 !important;

  :deep(.v-overlay__content) {
    margin-bottom: 0 !important;
  }
}

.dialog-v2-content {
  :deep(.v-card) {
    border-radius: 24px 24px 0 0 !important;
  }
}

.dialog-standard-content {
  // Standard centered dialog styling
}
</style>
